import {
  AdornedInput,
  Adornment,
  Alert,
  DatePicker,
  DatePickerInput,
  FieldSet,
  FormField,
  FormHelp,
  Grid,
  Heading,
  IconCalendarOutline,
  Label,
  LabeledControl,
  Legend,
  Radio,
} from '@zillow/constellation';
import { Controller, useFormContext } from 'react-hook-form';
import { FIELD_IDS } from '../../../pages/TenantDrivenLeaseRenewal';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';
import { LeaseRenewalTermPreference } from '@zg-rentals/ts-leasing-client';
import { useLeaseWebApiHandler_getLease } from '@zg-rentals/ts-leasing-client-react-query';
import { addDays, isBefore, isValid } from 'date-fns';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';

const INVALID_DATE_ERROR_MESSAGE = 'Enter a valid date in the format of January 1, 2026';

export function HowLongDoYouWantToRenewYourLease() {
  const { leaseId } = useLeasingUrlParams();
  const { watch, trigger } = useFormContext();
  const { data } = useLeaseWebApiHandler_getLease({ leaseId: leaseId as unknown as number });
  const leaseEndDate = data?.lease.endDate;
  const oneDayAfterLeaseEndDate = addDays(leaseEndDate, 1);

  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gridColumn="1 / -1">
      <Grid gridColumnEnd={{ sm_lte: 'span 12', md: 'span 8', xl: 'span 6' }}>
        <Controller
          name={FIELD_IDS.RENEWAL_TYPE}
          render={({ field: { value, onChange } }) => {
            return (
              <FieldSet
                required
                legend={
                  <Heading as={Legend} fontFamily="serif" level={3} marginBottom="sm">
                    How long do you want to renew your lease for?
                  </Heading>
                }
              >
                <LabeledControl
                  label={<Label>1 year</Label>}
                  control={
                    <Radio
                      name={FIELD_IDS.RENEWAL_TYPE}
                      value={LeaseRenewalTermPreference.ONE_YEAR}
                      checked={value == LeaseRenewalTermPreference.ONE_YEAR}
                      onChange={onChange}
                    />
                  }
                />
                <LabeledControl
                  label={<Label>2 years</Label>}
                  control={
                    <Radio
                      name={FIELD_IDS.RENEWAL_TYPE}
                      value={LeaseRenewalTermPreference.TWO_YEAR}
                      checked={value === LeaseRenewalTermPreference.TWO_YEAR}
                      onChange={onChange}
                    />
                  }
                />
                <LabeledControl
                  label={<Label>Month to month</Label>}
                  control={
                    <Radio
                      name={FIELD_IDS.RENEWAL_TYPE}
                      value={LeaseRenewalTermPreference.MONTH_TO_MONTH}
                      checked={value === LeaseRenewalTermPreference.MONTH_TO_MONTH}
                      onChange={onChange}
                    />
                  }
                />
                <LabeledControl
                  label={<Label>Choose a specific lease end date</Label>}
                  control={
                    <Radio
                      name={FIELD_IDS.RENEWAL_TYPE}
                      value={LeaseRenewalTermPreference.CUSTOM}
                      checked={value === LeaseRenewalTermPreference.CUSTOM}
                      onChange={onChange}
                    />
                  }
                />
              </FieldSet>
            );
          }}
        />
        {watch(FIELD_IDS.RENEWAL_TYPE) === LeaseRenewalTermPreference.CUSTOM && (
          <Controller
            name={FIELD_IDS.RENEWAL_END_DATE}
            rules={{
              validate: {
                isValidDate: (value) => {
                  if (!value) return INVALID_DATE_ERROR_MESSAGE;

                  let dateToValidate: Date;
                  if (value instanceof Date) {
                    dateToValidate = value;
                  } else if (typeof value === 'string') {
                    dateToValidate = new Date(value);
                  } else {
                    return INVALID_DATE_ERROR_MESSAGE;
                  }
                  if (!isValid(dateToValidate)) {
                    return INVALID_DATE_ERROR_MESSAGE;
                  }
                  return true;
                },
                isAfterLeaseEndDate: (value) => {
                  if (!value) return true;

                  let dateToValidate: Date;

                  if (value instanceof Date) {
                    dateToValidate = value;
                  } else if (typeof value === 'string') {
                    dateToValidate = new Date(value);
                  } else {
                    return true;
                  }
                  if (!isValid(dateToValidate)) return true;
                  if (isBefore(dateToValidate, oneDayAfterLeaseEndDate)) {
                    return `Enter a date after the current lease end date`;
                  }
                  return true;
                },
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              const handleChange = (newValue: Date | string) => {
                onChange(newValue);
                setTimeout(() => trigger(FIELD_IDS.RENEWAL_END_DATE), 0);
              };

              return (
                <FormField
                  required
                  label={<Label>Lease end date</Label>}
                  error={!!error?.message}
                  control={
                    <AdornedInput
                      input={
                        <DatePickerInput
                          value={value}
                          onChange={handleChange}
                          datepicker={
                            <DatePicker
                              dateFormat={DATE_PICKER_FORMAT}
                              disabledDates={(date: Date) =>
                                oneDayAfterLeaseEndDate ? isBefore(date, oneDayAfterLeaseEndDate) : false
                              }
                              defaultSelected={oneDayAfterLeaseEndDate}
                            />
                          }
                        />
                      }
                      rightAdornment={
                        <Adornment>
                          <IconCalendarOutline />
                        </Adornment>
                      }
                    />
                  }
                  description={<FormHelp>{error?.message as string}</FormHelp>}
                />
              );
            }}
          />
        )}
        {watch(FIELD_IDS.RENEWAL_TYPE) === LeaseRenewalTermPreference.MONTH_TO_MONTH && (
          <Alert
            marginTop="sm"
            appearance="warning"
            body="If you’re asking for concessions or a rent discount, your landlord may prefer a longer lease term"
          />
        )}
      </Grid>
    </Grid>
  );
}
